import { useId, useState } from "react";
import type { ApplyProjectBody, ApplyProjectView, Classmate } from "./api";

/**
 * The project form's questions — the ONE renderer, used by the student's page and by the
 * teacher's preview.
 *
 * 🔴 IT DRAWS WHAT THE SERVER SENDS, and since 2026-09-08 that is a teacher's own question
 * set rather than seven the code knew. This file knows how to draw four SHAPES — a choice, a
 * box of text, a link, and a time zone — and nothing about what any of them asks.
 *
 * 🔴 A TEACHER'S PREVIEW AND A STUDENT'S FORM ARE THE SAME COMPONENT, and that is the point.
 * [X] Before it existed, the Form tab showed only configuration — near-identical to the
 * intake round's — and the Copy link led to a page that, with this form off, renders the
 * INTAKE questions and nothing else: a teacher clicked through to check the teams form and
 * was shown the applicants one.
 *
 * ⚠️ `timezone` IS NOT A PRESENTATION CHOICE. It is the one answer the grouping UNDERSTANDS
 * rather than merely reads — hours that overlap cannot be derived from typed text — so it
 * offers the known zones AND accepts one somebody types, and the server derives the offset.
 */
export function ProjectQuestions({ form, draft, disabled, note, set, Q, Pills, Other,
                                  classmates }: {
  form: ApplyProjectView["form"];
  draft: ApplyProjectBody;
  /**
   * Who a Teammates question may pick. `undefined` is the TEACHER'S preview, which has no
   * student to exclude and says what a student would see instead of listing anybody.
   */
  classmates?: Classmate[];
  /**
   * 🔴 PER QUESTION, NOT ONE BOOLEAN FOR THE FORM — and a single boolean is what blocked an
   * earlier attempt at this. Once teams are published the server refuses an edit only to a
   * question the MODEL read (`grouping: true`); the others may still change, because they were
   * never part of what the grouping was built from. A blanket flag cannot express that, so the
   * client was stricter than the server and the disagreement was written down rather than
   * fixed. This is the seam that lets them agree.
   */
  disabled: (q: ApplyProjectView["form"]["questions"][number]) => boolean;
  /** Why this particular question is fixed, when it is. Rendered by the injected `Q`. */
  note?: (q: ApplyProjectView["form"]["questions"][number]) => string;
  set: (id: string, v: string | string[]) => void;
  // ⚠️ THE PRESENTATION PRIMITIVES ARE INJECTED, because the student page and the console
  // dress a question differently — one is a public form on a phone, the other a card in a
  // settings page. What must not differ is the QUESTIONS, and those come from the server.
  // ⚠️ WIDENED, NOT REPLACED. `index`, `answered` and `note` are optional, so the console's
  // own `Q` — which the teacher's preview passes and which knows none of them — still
  // satisfies this type. The student's surface reads them; the preview ignores them.
  Q: (p: { label: string; hint: string; optional?: boolean; index?: number;
           answered?: boolean; note?: string;
           children: React.ReactNode }) => React.ReactElement;
  Pills: (p: { options: string[]; value: string; disabled: boolean; label?: string;
               onChange: (v: string) => void }) => React.ReactElement;
  Other: (p: { label: string; hint: string; options: string[]; value: string;
               disabled: boolean; zone?: boolean; index?: number; answered?: boolean;
               note?: string; other_max?: number;
               onChange: (v: string) => void }) => React.ReactElement;
}) {
  return (
    <>
      {form.questions.map((q, i) => {
        const given = draft[q.id];
        const value = typeof given === "string" ? given : "";
        // ⚠️ ANY NON-EMPTY VALUE, required or not. It is the student's sense of progress, not
        // a validity check — the footer counts the different thing, and the two must agree
        // arithmetically rather than being one number used twice.
        const answered = !isEmpty(given);
        const off = disabled(q);
        const why = note?.(q) ?? "";
        if (q.kind === "teammates") {
          return (
            <Q key={q.id} label={q.label} hint={q.hint} optional={!q.required}
               index={i + 1} answered={answered} note={why}>
              <TeammatePicker label={q.label} classmates={classmates}
                              value={Array.isArray(given) ? given : []}
                              most={q.max_picks ?? 1} disabled={off}
                              onChange={(v) => set(q.id, v)} />
            </Q>
          );
        }
        if (q.kind === "timezone") {
          return (
            <Other key={q.id} label={q.label} hint={q.hint} options={q.options ?? []}
                   value={value} disabled={off} zone index={i + 1} answered={answered}
                   note={why} other_max={form.other_max}
                   onChange={(v) => set(q.id, v)} />
          );
        }
        return (
          // ⚠️ `optional` IS A PROP, NOT A SUFFIX ON THE LABEL. It used to append
          // "(optional)" to the text, which the intake form renders as an OPTIONAL chip
          // beside the label — two spellings of one fact on two forms in one product. It
          // was invisible while "Additional notes" was the only optional question and
          // nobody had scrolled that far; Supporting links joined it on 2026-09-09.
          <Q key={q.id} label={q.label} hint={q.hint} optional={!q.required}
             index={i + 1} answered={answered} note={why}>
            {q.kind === "choice" ? (
              <Pills options={q.options ?? []} value={value} disabled={off}
                     label={q.label} onChange={(v) => set(q.id, v)} />
            ) : q.kind === "link" ? (
              <input className="input wide" type="url" value={value} placeholder="https://"
                     disabled={off}
                     onChange={(e) => set(q.id, e.target.value)} />
            ) : (
              // ⚠️ `maxLength` IS THE SERVER'S NUMBER, so the box stops where the validator
              // refuses rather than letting somebody write past it and be told on submit.
              <textarea className="ta" rows={3} value={value} maxLength={q.max ?? 500}
                        disabled={off}
                        onChange={(e) => set(q.id, e.target.value)} />
            )}
          </Q>
        );
      })}
    </>
  );
}

/** Whether an answer is nothing — "", whitespace, or no picks. One rule for every reader. */
export function isEmpty(v: string | string[] | undefined): boolean {
  return Array.isArray(v) ? v.length === 0 : !(v ?? "").trim();
}

/** An empty answer set for this form — every question present, nothing filled in. */
export function emptyAnswers(form: ApplyProjectView["form"]): ApplyProjectBody {
  return Object.fromEntries(form.questions.map((q) => [q.id, q.kind === "teammates" ? [] : ""]));
}

/**
 * Pick up to `most` classmates by name.
 *
 * 🔴 CHECKBOXES WITH LABELS, NOT BUTTONS THAT LOOK LIKE THEM, for the reason the language
 * matrix gives: a screen reader announces a named checkbox and its state; a styled button is a
 * word with no state. The chosen ones repeat above the list as chips that remove, so a pick is
 * visible without scrolling a hundred names.
 * ⚠️ AT THE LIMIT THE REST ARE DISABLED AND THE COUNT SAYS WHY. The server refuses a list over
 * the limit too; this is the affordance, that is the gate.
 * ⚠️ A PICK THE LIST NO LONGER HOLDS — somebody who left the roster — still shows as a chip
 * and can be removed; it is never silently dropped from what the student sends.
 */
export function TeammatePicker({ label, classmates, value, most, disabled, onChange }: {
  label: string; classmates: Classmate[] | undefined; value: string[]; most: number;
  disabled: boolean; onChange: (v: string[]) => void;
}) {
  const [find, setFind] = useState("");
  const countId = useId();
  if (classmates === undefined) {
    return (
      <p className="small muted mates-note">
        Each student sees the classmates on this cohort&apos;s roster here, and picks up
        to {most}. Nobody is told who picked them.
      </p>
    );
  }
  const names = new Map(classmates.map((c) => [c.id, c.name]));
  const chosen = new Set(value);
  const full = value.length >= most;
  const needle = find.trim().toLowerCase();
  const shown = classmates.filter((c) => !needle || c.name.toLowerCase().includes(needle));
  const toggle = (id: string) =>
    onChange(chosen.has(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="mates-pick">
      {value.length > 0 && (
        <div className="mates-chosen">
          {value.map((id) => (
            <button key={id} type="button" className="mate-chip" disabled={disabled}
                    aria-label={`Remove ${names.get(id) ?? "this classmate"}`}
                    onClick={() => onChange(value.filter((x) => x !== id))}>
              {names.get(id) ?? "someone no longer listed"}
              <span aria-hidden="true"> ×</span>
            </button>
          ))}
        </div>
      )}
      <p className="small muted" id={countId}>
        {value.length} of {most} picked
        {full && !disabled ? " — remove one to pick somebody else." : "."}
      </p>
      {classmates.length === 0 ? (
        <p className="small muted mates-note">
          Nobody is listed yet. Your teacher syncs the class list; check back later.
        </p>
      ) : (
        <>
          <input className="input wide" type="search" value={find} disabled={disabled}
                 placeholder="Search by name" aria-label={`Search classmates — ${label}`}
                 aria-describedby={countId}
                 onChange={(e) => setFind(e.target.value)} />
          <ul className="mates-list">
            {shown.map((c) => (
              <li key={c.id}>
                <label className={`mate-opt${chosen.has(c.id) ? " on" : ""}`}>
                  <input type="checkbox" checked={chosen.has(c.id)}
                         disabled={disabled || (!chosen.has(c.id) && full)}
                         onChange={() => toggle(c.id)} />
                  <span>{c.name}</span>
                </label>
              </li>
            ))}
            {shown.length === 0 && <li className="small muted">Nobody matches that.</li>}
          </ul>
        </>
      )}
    </div>
  );
}
